import { models, type Nullish, utils } from 'cesg-shared';
import { computed, ref } from 'vue';
import type { AtaFieldKey, AtaFormData } from '../types';

// --- Dispatch action union — the sole mutation path ---

export type AtaDetailsDispatchAction =
  | { type: 'update-field'; field: AtaFieldKey; value: unknown }
  | { type: 'set-status'; status: models.domain.AtaStatusType }
  | { type: 'reset-baseline' }
  | { type: 'stamp-revision-date' }
  | { type: 'load'; data: AtaFormData; createNewAta: boolean };

const isVerdictStatus = (status: Nullish<models.domain.AtaStatusType>) =>
  status === models.domain.AtaStatusType.Approved ||
  status === models.domain.AtaStatusType.Rejected;

export const createAtaDetailsStore = (
  initialData: AtaFormData,
  initialCreateNewAta: boolean,
) => {
  const formData = ref<AtaFormData>(utils.json.deepCopy(initialData));
  // Deep copy so the baseline is independent of formData — otherwise the
  // shared `costs` array means row add/delete/edit mutates both and the dirty
  // check never detects it.
  const originalFormData = ref<AtaFormData>(utils.json.deepCopy(initialData));
  const createNewAta = ref<boolean>(initialCreateNewAta);

  function dispatch(action: AtaDetailsDispatchAction): void {
    switch (action.type) {
      case 'update-field': {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (formData.value as any)[action.field] = action.value;

        // Löpande pricing has no forecast question, so clear any stale answer.
        if (
          action.field === 'pricingModel' &&
          action.value === models.domain.AtaPricingModel.Lopande
        ) {
          formData.value.shouldBeForecasted = null;
        }
        break;
      }
      case 'set-status': {
        const previousStatus = formData.value.status;
        formData.value.status = action.status;

        // The answer-received date is only sticky once persisted on the server.
        // If the server already has a value, never auto-modify it.
        if (originalFormData.value.answerReceivedDate) {
          break;
        }

        if (isVerdictStatus(action.status)) {
          // Entering a verdict: stamp today if not already filled.
          if (!formData.value.answerReceivedDate) {
            formData.value.answerReceivedDate = new Date().toLocaleDateString(
              'sv-SE',
            );
          }
        } else if (isVerdictStatus(previousStatus)) {
          // Leaving an unsaved verdict: drop the auto-filled date.
          formData.value.answerReceivedDate = null;
        }
        break;
      }
      case 'reset-baseline': {
        originalFormData.value = utils.json.deepCopy(formData.value);
        break;
      }
      case 'stamp-revision-date': {
        // Stamp the revision date when saving changes to an existing ÄTA, unless
        // the user manually edited it this session — a manual value wins. Only
        // the user input and this stamp ever touch revisionDate, so a value
        // differing from the loaded baseline means the user changed it; leave it.
        if (
          !createNewAta.value &&
          formData.value.revisionDate === originalFormData.value.revisionDate
        ) {
          formData.value.revisionDate = new Date().toLocaleDateString('sv-SE');
        }
        break;
      }
      case 'load': {
        formData.value = utils.json.deepCopy(action.data);
        originalFormData.value = utils.json.deepCopy(action.data);
        createNewAta.value = action.createNewAta;
        break;
      }
    }
  }

  // A cost's `amount` is derived from its other fields and recomputed on load,
  // where a float round-trip through the DB can shift it by an ULP. The server
  // recomputes it on save, so leave it out of the comparison entirely.
  const serializeComparable = (data: AtaFormData) =>
    JSON.stringify(data, (key, value) =>
      key === 'amount' ? undefined : value,
    );

  const isDirty = computed(
    () =>
      serializeComparable(formData.value) !==
      serializeComparable(originalFormData.value),
  );

  return { formData, originalFormData, createNewAta, dispatch, isDirty };
};
